import { Checkbox, FormControlLabel, TextField, Tooltip } from '@mui/material';
import Dialog from 'components/Dialog';
import { AppSelectBox } from 'components/FormElements';
import Spacing from 'components/Spacing';
import { AccountSectionLabels } from 'further-ui/labels';
import { account as rules } from 'further-ui/validations';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Schema } from 'further-types/lp/account';
import useAccountsManagement from 'hooks/lp/data/accounts/useAccountsManagement';
import { Row } from 'components/Layout';

type FormValues = z.infer<typeof rules.CreateAccount>;

type Props = {
  vehicleId: string;
  onClose: () => void;
  prepaidExpenseAccountId?: string;
  deferredIncomeAccountId?: string;
  bankTransferReceivableAccountId?: string;
  account?: {
    id: string;
    section: Schema.AccountSection;
    accountName: string;
    accountCode: string;
    payable: boolean;
    receivable: boolean;
    isPrepaidExpenses: boolean;
    isDeferredIncome: boolean;
    isBankTransferReceivable: boolean;
    transactionsCount: number;
  };
};

const sections = Object.entries(AccountSectionLabels).map(([key, label]) => ({
  key,
  label,
}));

const AddEditAccount: React.FC<Props> = ({
  vehicleId,
  account,
  onClose,
  prepaidExpenseAccountId,
  deferredIncomeAccountId,
  bankTransferReceivableAccountId,
}) => {
  const { control, handleSubmit, watch } = useForm<FormValues>({
    resolver: zodResolver(rules.CreateAccount),
    defaultValues: {
      section: account?.section,
      accountName: account?.accountName ?? '',
      accountCode: account?.accountCode ?? '',
      payable: account?.payable ?? false,
      receivable: account?.receivable ?? false,
      isPrepaidExpenses: account?.isPrepaidExpenses ?? false,
      isDeferredIncome: account?.isDeferredIncome ?? false,
      isBankTransferReceivable: account?.isBankTransferReceivable ?? false,
    },
  });

  const { createAccount, updateAccount } = useAccountsManagement({
    onSuccess: onClose,
  });

  const onSubmit = (data: FormValues) => {
    if (account) {
      updateAccount.mutate({
        ...data,
        payable: data.payable ?? account.payable,
        receivable: data.receivable ?? account.receivable,
        isPrepaidExpenses: data.isPrepaidExpenses ?? account.isPrepaidExpenses,
        isDeferredIncome: data.isDeferredIncome ?? account.isDeferredIncome,
        isBankTransferReceivable:
          data.isBankTransferReceivable ?? account.isBankTransferReceivable,
        id: account.id,
        vehicleId,
      });
    } else {
      createAccount.mutate({
        ...data,
        vehicleId,
      });
    }
  };

  const isPayable = watch('payable');
  const isReceivable = watch('receivable');
  const isPrepaidExpenses = watch('isPrepaidExpenses');
  const isDeferredIncome = watch('isDeferredIncome');
  const isBankTransferReceivable = watch('isBankTransferReceivable');
  const prepaidExpenseAccountExists = !!(
    prepaidExpenseAccountId && prepaidExpenseAccountId !== account?.id
  );
  const deferredIncomeAccountExists = !!(
    deferredIncomeAccountId && deferredIncomeAccountId !== account?.id
  );
  const bankTransferReceivableAccountExists = !!(
    bankTransferReceivableAccountId &&
    bankTransferReceivableAccountId !== account?.id
  );

  return (
    <Dialog
      title={account ? 'Edit account' : 'Add account'}
      open
      onConfirm={handleSubmit(onSubmit)}
      onClose={onClose}
      pending={createAccount.isPending || updateAccount.isPending}
    >
      <form>
        <Spacing size="sm">
          <Controller
            name="section"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AppSelectBox
                {...field}
                data={sections}
                valueKey="key"
                labelKey="label"
                placeholder="General ledger section"
                error={!!error}
                helperText={error?.message}
                fullWidth
                disabled={!!account}
              />
            )}
          />
          <Controller
            name="accountCode"
            control={control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <TextField
                {...rest}
                inputRef={ref}
                placeholder="Account code"
                error={!!error}
                helperText={error?.message}
                fullWidth
              />
            )}
          />
          <Controller
            name="accountName"
            control={control}
            render={({ field: { ref, ...rest }, fieldState: { error } }) => (
              <TextField
                {...rest}
                inputRef={ref}
                placeholder="Account name"
                error={!!error}
                helperText={error?.message}
                fullWidth
              />
            )}
          />
          <Row fullWidth>
            <FormControlLabel
              control={
                <Controller
                  name="payable"
                  control={control}
                  render={({ field: { value, ...rest } }) => (
                    <Checkbox checked={value} {...rest} />
                  )}
                  disabled={
                    isReceivable ||
                    isPrepaidExpenses ||
                    isDeferredIncome ||
                    (!!account?.payable && account?.transactionsCount > 0)
                  }
                />
              }
              label="Payable"
            />
            <FormControlLabel
              control={
                <Controller
                  name="receivable"
                  control={control}
                  render={({ field: { value, ...rest } }) => (
                    <Checkbox
                      checked={value || isBankTransferReceivable}
                      {...rest}
                    />
                  )}
                  disabled={
                    isPayable ||
                    isPrepaidExpenses ||
                    isDeferredIncome ||
                    isBankTransferReceivable ||
                    (!!account?.receivable && account?.transactionsCount > 0)
                  }
                />
              }
              label="Receivable"
            />
            <Tooltip
              title={
                prepaidExpenseAccountExists
                  ? 'Only one account can be used to record prepaid expenses.'
                  : ''
              }
            >
              <FormControlLabel
                control={
                  <Controller
                    name="isPrepaidExpenses"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <Checkbox checked={value} {...rest} />
                    )}
                    disabled={
                      isDeferredIncome ||
                      isReceivable ||
                      isPayable ||
                      prepaidExpenseAccountExists ||
                      isBankTransferReceivable ||
                      (!!account?.isPrepaidExpenses &&
                        account?.transactionsCount > 0)
                    }
                  />
                }
                label="Prepaid expenses"
              />
            </Tooltip>
            <Tooltip
              title={
                deferredIncomeAccountExists
                  ? 'Only one account can be used to record deferred income.'
                  : ''
              }
            >
              <FormControlLabel
                control={
                  <Controller
                    name="isDeferredIncome"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <Checkbox checked={value} {...rest} />
                    )}
                    disabled={
                      isPrepaidExpenses ||
                      isPayable ||
                      isReceivable ||
                      deferredIncomeAccountExists ||
                      isBankTransferReceivable ||
                      (!!account?.isDeferredIncome &&
                        account?.transactionsCount > 0)
                    }
                  />
                }
                label="Deferred income"
              />
            </Tooltip>
          </Row>
          <Row fullWidth>
            <Tooltip
              title={
                bankTransferReceivableAccountExists
                  ? 'Only one account can be used to record bank transfer receivables.'
                  : ''
              }
            >
              <FormControlLabel
                control={
                  <Controller
                    name="isBankTransferReceivable"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <Checkbox checked={value} {...rest} />
                    )}
                    disabled={
                      !isReceivable ||
                      bankTransferReceivableAccountExists ||
                      (!!account?.isBankTransferReceivable &&
                        account?.transactionsCount > 0)
                    }
                  />
                }
                label="Bank Transfer receivable"
              />
            </Tooltip>
          </Row>
        </Spacing>
      </form>
    </Dialog>
  );
};

export default AddEditAccount;
